import React from 'react';
import type { TFunction } from 'i18next';
import {
  institutionTabDefs,
  institutionTabLabelKey,
  INSTITUTION_RH_SIDEBAR_ENTRIES,
  INSTITUTION_SECTION_LABEL_KEYS,
  INSTITUTION_NAV_SECTION_ORDER,
  formateurDashboardTabPath,
  formateurSettingsTabPath,
  formateurTabPath,
  institutionTabSurface,
  type InstitutionNavSectionId,
  type InstitutionTabId,
} from '@/lib/institution-tab-registry';
import { INSTITUTION_MENU_FLAGS, isInstitutionMenuEnabled } from '@/lib/institution-menu-flags';
import { isLyrraOne } from '@/lib/platform';

export interface FormateurInstitutionNavItem {
  labelKey: string;
  path: string;
  icon: React.ElementType;
  section: InstitutionNavSectionId;
}

function tabEnabled(
  tab: (typeof institutionTabDefs)[number],
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
): boolean {
  if (tab.menuFlag && hasInstitution && !isInstitutionMenuEnabled(menuFlags, tab.menuFlag)) return false;
  return true;
}

export function getFormateurInstitutionHubTabs(
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
  surface: 'dashboard' | 'settings' = 'dashboard',
) {
  return institutionTabDefs.filter((tab) => {
    if (tab.id === 'home') return false;
    if (isLyrraOne() && institutionTabSurface(tab.id) !== surface) return false;
    return tabEnabled(tab, menuFlags, hasInstitution);
  });
}

export function buildFormateurOperationalNavItems(
  _t: TFunction,
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
): FormateurInstitutionNavItem[] {
  const items: FormateurInstitutionNavItem[] = [];

  for (const tab of institutionTabDefs) {
    if (tab.id === 'home') continue;
    if (isLyrraOne() && institutionTabSurface(tab.id) !== 'dashboard') continue;
    if (!tabEnabled(tab, menuFlags, hasInstitution)) continue;
    items.push({
      labelKey: institutionTabLabelKey(tab.id),
      path: formateurDashboardTabPath(tab.id),
      icon: tab.icon,
      section: tab.section,
    });
  }

  return items;
}

export function buildFormateurSettingsNavItems(
  _t: TFunction,
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
): FormateurInstitutionNavItem[] {
  const items: FormateurInstitutionNavItem[] = [];

  for (const tab of institutionTabDefs) {
    if (tab.id === 'home') continue;
    if (isLyrraOne() && institutionTabSurface(tab.id) !== 'settings') continue;
    if (!isLyrraOne()) continue;
    if (!tabEnabled(tab, menuFlags, hasInstitution)) continue;
    items.push({
      labelKey: institutionTabLabelKey(tab.id),
      path: formateurSettingsTabPath(tab.id),
      icon: tab.icon,
      section: tab.section,
    });
  }

  return items;
}

/** Sidebar Studio / legacy : tous les onglets institution. */
export function buildFormateurInstitutionNavItems(
  t: TFunction,
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
): FormateurInstitutionNavItem[] {
  if (isLyrraOne()) {
    return buildFormateurOperationalNavItems(t, menuFlags, hasInstitution);
  }

  const items: FormateurInstitutionNavItem[] = [];

  for (const tab of institutionTabDefs) {
    if (tab.id === 'home') continue;
    if (!tabEnabled(tab, menuFlags, hasInstitution)) continue;
    items.push({
      labelKey: institutionTabLabelKey(tab.id),
      path: formateurTabPath(tab.id),
      icon: tab.icon,
      section: tab.section,
    });
  }

  for (const rh of INSTITUTION_RH_SIDEBAR_ENTRIES) {
    if (hasInstitution && !isInstitutionMenuEnabled(menuFlags, rh.menuFlag)) continue;
    items.push({
      labelKey: rh.labelKey,
      path: formateurDashboardTabPath(rh.id),
      icon: rh.icon,
      section: rh.section,
    });
  }

  items.unshift({
    labelKey: 'institution.tabs.home',
    path: formateurDashboardTabPath('home'),
    icon: institutionTabDefs[0].icon,
    section: 'general',
  });

  return items;
}

export function groupFormateurInstitutionNav(
  items: FormateurInstitutionNavItem[],
): Array<{ section: InstitutionNavSectionId; label: string; items: FormateurInstitutionNavItem[] }> {
  return INSTITUTION_NAV_SECTION_ORDER.map((section) => ({
    section,
    label: INSTITUTION_SECTION_LABEL_KEYS[section],
    items: items.filter((i) => i.section === section),
  })).filter((g) => g.items.length > 0);
}

export function formateurInstitutionNavFlat(
  t: TFunction,
  menuFlags: Record<string, boolean> | undefined,
  hasInstitution: boolean,
): FormateurInstitutionNavItem[] {
  return buildFormateurInstitutionNavItems(t, menuFlags, hasInstitution);
}

/** Alias pour compat sidebar */
export { INSTITUTION_MENU_FLAGS };
